import { updateConfigSettings } from '@api/portalServices';
import type { Setting } from '@api/types';
import type { ApiError } from '@errorHandler/errors';
import {
  AuthnLevel,
  FieldNames,
  type FormValues,
  InstanceKey,
  updateSettingsTemplate,
  updateUserCanShowLiveFeedSettingsTemplate,
  UserCanShowLiveFeedValue,
} from '@portal/containers/security/constants';
import { useMutation } from '@tanstack/react-query';
import { sleep } from '@utils/utils';

export type UpdateConfigParams = {
  ussSetting: Setting | undefined;
  iamSetting: Setting | undefined;
  userCanShowLiveFeedSetting: Setting | undefined;
  formValues: FormValues;
};

export function computeNewSettingValues(formValues: FormValues): {
  ussNewSettingValue: AuthnLevel;
  iamNewSettingValue: AuthnLevel;
} {
  return {
    ussNewSettingValue: formValues[FieldNames.USS_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : formValues[FieldNames.USS_VALUE],
    iamNewSettingValue: formValues[FieldNames.IAM_ONE_FACTOR_VALUE]
      ? AuthnLevel.ICP_MFA_IF_ENROLLED
      : formValues[FieldNames.IAM_VALUE],
  };
}

export function buildUpdatedSettings({
  ussSetting,
  iamSetting,
  userCanShowLiveFeedSetting,
  formValues,
}: UpdateConfigParams): Setting[] {
  const settings: Setting[] = [];
  const { ussNewSettingValue, iamNewSettingValue } = computeNewSettingValues(formValues);
  if (ussSetting?.value !== ussNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: ussSetting?.settingId,
      instanceKey: InstanceKey.USS,
      value: ussNewSettingValue,
    });
  }

  if (iamSetting?.value !== iamNewSettingValue) {
    settings.push({
      ...updateSettingsTemplate,
      settingId: iamSetting?.settingId,
      instanceKey: InstanceKey.IAM,
      value: iamNewSettingValue,
    });
  }

  const userCanShowLiveFeedNewSettingValue = formValues[FieldNames.USER_CAN_SHOW_LIVE_FEED_VALUE]
    ? UserCanShowLiveFeedValue.ON
    : UserCanShowLiveFeedValue.OFF;
  // Diff-only update: the setting is only written when the value has changed from what
  // the backend returned. For tenants without the face-recognition entitlement,
  // userCanShowLiveFeedSetting is undefined and the form default is ON, so
  // shouldUpdateUserCanShowLiveFeedSetting will always be false — the field is
  // never submitted to the backend even though it remains in the form state.
  const shouldUpdateUserCanShowLiveFeedSetting = userCanShowLiveFeedSetting
    ? userCanShowLiveFeedSetting.value.toLowerCase() !== userCanShowLiveFeedNewSettingValue
    : userCanShowLiveFeedNewSettingValue !== UserCanShowLiveFeedValue.ON;

  if (shouldUpdateUserCanShowLiveFeedSetting) {
    settings.push({
      ...updateUserCanShowLiveFeedSettingsTemplate,
      instanceKey:
        userCanShowLiveFeedSetting?.instanceKey ??
        updateUserCanShowLiveFeedSettingsTemplate.instanceKey,
      settingId: userCanShowLiveFeedSetting?.settingId,
      value: userCanShowLiveFeedNewSettingValue,
    });
  }

  return settings;
}

export const useUpdateConfigSettings = () => {
  return useMutation<void, ApiError, UpdateConfigParams>({
    mutationFn: async params => {
      const settings = buildUpdatedSettings(params);
      if (settings.length > 0) {
        await updateConfigSettings({ settings });
        await sleep(500);
      }
    },
    retry: false,
  });
};
